import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import AppConfig from '../AppConfig';
import ReCAPTCHA from 'react-google-recaptcha';
import React, { useContext, useRef, useState } from 'react';
import { sendSupportMessage } from '../rest/BackendApi';
import UserContext from '../UserContext';
import { PageContainer, PageHeader, Section } from '../components/Page';

const RESOURCES = [
  {
    icon: <MenuBookIcon />,
    title: 'Knowledge base',
    description: 'Step-by-step guides for setting up and using BAS Ready.',
    href: 'https://basready.com.au/knowledge-base/',
  },
  {
    icon: <QuizIcon />,
    title: 'FAQ',
    description: 'Quick answers to common questions.',
    href: 'https://basready.com.au/FAQ/',
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Errors = { email?: string; message?: string; captcha?: string; send?: string };

export function validateSupportForm(values: { message: string; email: string; needsEmail: boolean }): Errors {
  const errors: Errors = {};
  if (values.needsEmail && !EMAIL_PATTERN.test(values.email.trim())) {
    errors.email = 'Enter the email address we should reply to';
  }
  if (!values.message.trim()) {
    errors.message = 'Tell us what you need help with';
  }
  return errors;
}

export default function SupportPage() {
  const userContext = useContext(UserContext);
  const user = userContext?.user;
  const recaptcha = useRef<ReCAPTCHA>(null);
  const [message, setMessage] = useState('');
  const [emailFrom, setEmailFrom] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validation = validateSupportForm({ message, email: emailFrom, needsEmail: !user });
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    const captcha = recaptcha.current?.getValue();
    if (!captcha) {
      setErrors({ captcha: 'Please tick “I’m not a robot” before sending.' });
      return;
    }
    setErrors({});
    setSending(true);
    try {
      await sendSupportMessage(message.trim(), captcha, user ? undefined : emailFrom.trim());
      setSent(true);
      setMessage('');
    } catch (e) {
      // Keep what they typed so they can retry.
      setErrors({ send: "We couldn't send your message. Please try again in a moment." });
      recaptcha.current?.reset();
    }
    setSending(false);
  };

  const replyTo = user ? user.email : emailFrom.trim();

  return (
    <PageContainer maxWidth={1000}>
      <PageHeader title="Support & Help" description="Find an answer in our guides, or send us a message and we'll get back to you." />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.4fr' }, gap: 2, alignItems: 'start' }}>
        <Section title="Help resources">
          <Stack gap={1}>
            {RESOURCES.map(resource => (
              <Link
                key={resource.title}
                href={resource.href}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px solid #e0e0e0',
                  color: 'inherit',
                  '&:hover': { borderColor: '#007ca3', backgroundColor: '#f5fafc' },
                }}
              >
                <Box sx={{ color: '#007ca3', mt: '2px' }}>{resource.icon}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {resource.title} <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {resource.description}
                  </Typography>
                </Box>
              </Link>
            ))}
          </Stack>
        </Section>

        <Section title="Contact us">
          {sent ? (
            <Stack gap={2} alignItems="flex-start">
              <Alert severity="success" sx={{ width: '100%' }}>
                Message sent. We'll reply to <b>{replyTo}</b>.
              </Alert>
              <Button onClick={() => setSent(false)}>Send another message</Button>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {errors.send && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.send}
                </Alert>
              )}
              {user ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We'll reply to <b>{user.email}</b>
                </Typography>
              ) : (
                <TextField
                  value={emailFrom}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  onChange={e => setEmailFrom(e.target.value)}
                  label="Your email"
                  type="email"
                  autoComplete="email"
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
              )}
              <TextField
                multiline
                value={message}
                error={!!errors.message}
                helperText={errors.message}
                required
                onChange={e => setMessage(e.target.value)}
                label="How can we help?"
                placeholder="Include the organisation name and what you were trying to do."
                minRows={6}
                fullWidth
                sx={{ mb: 2 }}
              />
              <ReCAPTCHA ref={recaptcha} sitekey={AppConfig.recaptchaSiteKey} />
              {errors.captcha && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }} role="alert">
                  {errors.captcha}
                </Typography>
              )}
              <Button sx={{ mt: 2 }} variant="contained" type="submit" endIcon={<SendIcon />} disabled={sending}>
                {sending ? 'Sending…' : 'Send message'}
              </Button>
            </Box>
          )}
        </Section>
      </Box>
    </PageContainer>
  );
}
